/**
 * Client-side mirror of the server's `validateConditionalTrade` in
 * routes/skill-mapper/server/conditional-trades.mjs. Returns an array of problem
 * strings; an empty array means the conditional is fine to save.
 *
 * The duplication is deliberate: server .mjs cannot be imported from client .js. Keep
 * the two lists in sync when adding new validation rules.
 */
export function validateConditionalTrade(conditional) {
    const problems = []
    if (!conditional || typeof conditional !== "object") return ["Conditional is missing or not an object"]
    if (!conditional.name || !String(conditional.name).trim()) problems.push("Name is required")
    if (!Array.isArray(conditional.conditions) || conditional.conditions.length === 0) {
        problems.push("At least one condition is required")
    } else {
        const allConditionsHaveField = conditional.conditions.every(conditionHasFieldAndOperator)
        if (!allConditionsHaveField) problems.push("Every condition needs a field and an operator")
        const allConditionsHaveValue = conditional.conditions.every(conditionHasRequiredValue)
        if (!allConditionsHaveValue) problems.push("Every condition needs a value")
    }
    if (conditional.combinator !== "and" && conditional.combinator !== "or") {
        problems.push("Combinator must be 'and' or 'or'")
    }
    if (!conditional.tradeId) problems.push("Target trade is required")
    return problems
}

function conditionHasFieldAndOperator(condition) {
    return condition.field && condition.operator
}

function conditionHasRequiredValue(condition) {
    if (!condition?.field || !condition?.operator) return true
    if (conditionDoesNotNeedValue(condition)) return true
    return hasMeaningfulValue(condition.value, condition)
}

function conditionDoesNotNeedValue(condition) {
    if (condition.field === "taskOptionality") return true
    if (condition.field === "scheduleRetired") return true
    if (condition.field === "hasCompliance") return true
    return condition.field === "complianceItems" && condition.operator === "has any"
}

function hasMeaningfulValue(value, condition) {
    if (value == null) return false
    if (Array.isArray(value)) return value.some(hasMeaningfulArrayValue)
    if (typeof value === "string") return value.trim().length > 0
    if (typeof value === "number") return Number.isFinite(value)
    if (typeof value === "boolean") return true
    if (typeof value === "object") return hasMeaningfulObjectValue(value, condition)
    return true
}

function hasMeaningfulArrayValue(value) {
    if (value == null) return false
    if (typeof value === "string") return value.trim().length > 0
    if (typeof value === "object") return Object.keys(value).length > 0
    return true
}

function hasMeaningfulObjectValue(value, condition) {
    if (condition.operator === "between") {
        return Number.isFinite(value.min) && Number.isFinite(value.max)
    }
    if (condition.field === "frequency") {
        return Number.isFinite(value.interval) && !!value.period
    }
    if (condition.field === "regimeField" || condition.field === "assetField") {
        return !!value.field && Array.isArray(value.values) && value.values.some(hasMeaningfulArrayValue)
    }
    return Object.values(value).some((entry) => hasMeaningfulValue(entry, condition))
}
